/* eslint-disable dot-notation */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Button } from 'react-bootstrap';
import { withRouter } from 'react-router';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faChevronCircleRight } from '@fortawesome/pro-solid-svg-icons/faChevronCircleRight';

import Placeholder from 'components/Placeholder';
import FA from 'utils/FA';
import {
  reviewContacts as reviewContactsDuck,
  reviewContactsFilters,
  contacts as contactsDuck,
  fields as fieldsDuck
} from 'ducks';
import withSorting from 'hocs/withSorting';
import withPagination from 'hocs/withPagination';
import withFiltering from 'hocs/withFiltering';
import {
  dependentDuck,
  duckify,
  filterDuck,
  singleDuck
} from 'utils/duckify';
import Filters from 'components/Filters/Filters';
import Pagination from 'components/ContactList/Pagination';
import useBulkActions from 'hooks/useBulkActions';
import ContactList from 'components/ContactList/ContactList';
import { ContactRow, ContactRowWithComments } from '../ContactList/ContactRows';
import Routes from '../../../routes';
import ReviewDecisionCell from './ReviewDecisionCell';
import ReadingProgressCell from './ReadingProgressCell';
import ReviewRatingCell from './ReviewRatingCell';
import AdmissionWorkflowNavigation from '../ContactList/AdmissionWorkflowNavigation';
import './styles/CandidatesInReviewContactList.scss';

const CandidatesInReviewContactList = props => {
  const {
    userFilter,
    reviewContacts,
    filters,
    sort,
    page,
    setPage,
    setFilters,
    onSort,
    updateContact,
    reviewDecisionField,
    reviewYear,
    updateFilters,
    duckMeta
  } = props;

  const {
    trackedItems,
    promptSelectAll,
    selectionMode,
    togglePageTracked,
    toggleTrackedItem,
    itemIsSelected
  } = useBulkActions(reviewContacts);

  const isSelectAll = selectionMode === 'all' || promptSelectAll;
  const isSelectNone = selectionMode !== 'all' && !trackedItems.length;

  useEffect(() => {
    // Call legacy jQuery helper to toggle bulk actions since they have not been migrated to react yet
    global.Header.toggleBulkActions(isSelectNone);
  }, [isSelectNone]);

  useEffect(() => {
    if (duckMeta.userFilter.ready) {
      setFilters(userFilter.filters);
    }
  }, [duckMeta.userFilter.ready]);

  const setReviewDecision = (student, decision) => {
    updateContact({
      ...student,
      review_decision: decision
    });
  };

  const makeRow = rowProps => {
    const { contact } = rowProps;
    const noReaders = contact['_review_progress'] && contact['_review_progress'] === 'No readers assigned';
    const className = noReaders ? 'review-no-readers' : '';

    if (contact['_review_comments'] && contact['_review_comments'].length > 0) {
      return (
        <ContactRowWithComments
          className={className}
          {...rowProps}
        />
      );
    }

    return (
      <ContactRow
        className={className}
        {...rowProps}
      />
    );
  };

  const generateColumns = fields => {
    const columns = [
      {
        header: () => (
          <input
            className='contact-list-header-checkbox bulk-toggle'
            type='checkbox'
            checked={isSelectAll}
            onChange={togglePageTracked}
          />
        ),
        type: 'input',
        value: (student) => (
          <td>
            <input
              className='contact-list-student-checkbox bulk-item'
              type='checkbox'
              value={student.id}
              checked={itemIsSelected(student.id)}
              onChange={toggleTrackedItem}
              name={'ids[]'}
            />
          </td>
        )
      },
      ...fields.filter(field => field.name !== '_review_status' && !field.hidden)
        .map(field => ({
          header: field.display_name,
          name: field.name,
          sortName: (field.sortable ? field.name : undefined),
          value: (student, props) => {
            if (field.name === 'display_name') {
              return (
                <td className={`review-field-${field.name}`}>
                  <a href={Routes.contact_review_path(student.id, { schoolYearId: reviewYear.id })}>
                    {student[field.name]}
                  </a>
                </td>
              );
            } else if (field.name === 'review_decision') {
              return (
                <ReviewDecisionCell
                  className={`review-field-${field.name}`}
                  decisions={reviewDecisionField.options}
                  initialSelected={student.review_decision || 'None'}
                  contact={student}
                  onChange={setReviewDecision}
                />
              );
            } else if (field.name === '_review_progress') {
              return (
                <ReadingProgressCell
                  className={`review-field-${field.name}`}
                  value={student[field.name]}
                  status={student['_review_status'].status}
                />
              );
            } else if (field.name.includes('_review_rating[')) {
              return (
                <ReviewRatingCell
                  className={`review-field-${field.name}`}
                  value={student[field.name]}
                  scores={student['_review_status'].scores[field.name]}
                />
              );
            } else if (field.name === '_review_comments' && student[field.name].length > 0) {
              return (
                <td className={`review-field-${field.name} comments`}>
                  <Button
                    className={`review-field-${field.name}__button`}
                    variant='link'
                    // eslint-disable-next-line react/prop-types
                    onClick={props.onClick}
                  >
                    Comments&nbsp;
                    <FA icon={faChevronCircleRight} />
                  </Button>
                </td>
              );
            } else if (field.name === 'review_comments'
              || (field.name.startsWith('calendar_') && field.name.endsWith('_date'))) {
              return (
                <td className={`review-field-${field.name}`}>
                  <div dangerouslySetInnerHTML={{ __html: student[field.name] }} />
                </td>
              );
            }
            return (
              <td className={`review-field-${field.name}`}>
                <div>{student[field.name]}</div>
              </td>
            );
          }
        }))
    ];

    return columns;
  };

  const totalPages = duckMeta.reviewContacts.ready ? Math.ceil(reviewContacts.total / reviewContacts.perPage) : 1;

  return (
    <div className='candidates-in-review-contact-list'>
      <div className='candidates-in-review-contact-list__navigation'>
        <AdmissionWorkflowNavigation
          canProspect
          canInquiry
          canReview
        />
        <Pagination
          hideFirstAndLastPageLinks
          totalPages={totalPages}
          page={page}
          setPage={setPage}
          placement={'top'}
        />
      </div>
      <Filters
        className={'candidates-in-review-contact-list__filters'}
        sublabel={duckMeta.reviewContacts.ready ? (
          <Button
            href={Routes.search_path('', { criteria_json: JSON.stringify(reviewContacts.search) })}
            variant='link'
            size='xs'
            className='review-contact-list__filter-link'
          >
            <FA icon={faSearch} nbsp />
            View in Search
          </Button>
        ) : null}
        fields={duckMeta.reviewContacts.ready ? (
          reviewContacts.additionalFields.filter_fields
            .filter(field => field.option_prompts.length > 0)
            .map(field => ({
              ...field,
              display_name: field.prompt,
              options: field.options.map((option, i) => [field.option_prompts[i], option])
            }))
        ) : []}
        selected={filters}
        submitFilters={newFilters => {
          setFilters(newFilters);
          updateFilters(userFilter, newFilters);
        }}
        itemsName='Contacts'
      />
      <Placeholder.text
        ready={duckMeta.reviewContacts.ready}
        width={400}
        content={() => (reviewContacts.length > 0 ? (
          <ContactList
            className='candidates-in-review-contact-list__ContactList'
            columns={generateColumns(reviewContacts.additionalFields.fields)}
            emptyMessage='No contacts meet the selected criteria.'
            contacts={reviewContacts}
            onSort={onSort}
            sort={sort}
            makeRow={makeRow}
          />
        ) : (
          <div className='candidates-in-review-contact-list__no-contacts'>
            No contacts meet the selected criteria.
          </div>
        ))}
      />
      <Pagination
        hideFirstAndLastPageLinks
        totalPages={totalPages}
        page={page}
        setPage={setPage}
        placement={'bottom'}
      />
    </div>
  );
};

CandidatesInReviewContactList.propTypes = {
  /** Current Review Year */
  reviewYear: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    review_enabled: PropTypes.bool
  }),

  /** List of contacts in review */
  reviewContacts: PropTypes.arrayOf(PropTypes.object),

  /** UpdateContact duck update dispatch */
  updateContact: PropTypes.func,

  /** Handles filter state changes */
  updateFilters: PropTypes.func,

  /** initial Filters set loaded from backend */
  userFilter: PropTypes.shape({
    filters: PropTypes.shape({}),
    id: PropTypes.string
  }),

  /** withFiltering HOC Props */
  filters: PropTypes.shape({}),
  setFilters: PropTypes.func,

  /** withPagination HOC Props */
  page: PropTypes.number,
  setPage: PropTypes.func,

  /** withSorting HOC Props */
  sort: PropTypes.shape({}),
  onSort: PropTypes.func,

  reviewDecisionField: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,

  /** allDucksReady prop */
  allDucksReady: PropTypes.bool,

  duckMeta: PropTypes.shape({
    reviewContacts: PropTypes.shape({ ready: PropTypes.bool }),
    reviewDecisionField: PropTypes.shape({ ready: PropTypes.bool }),
    userFilter: PropTypes.shape({ ready: PropTypes.bool })
  })
};

CandidatesInReviewContactList.defaultProps = {
  reviewYear: {},
  reviewContacts: [],
  userFilter: {},
  filters: {},
  page: 1,
  sort: {},
  updateContact: () => false,
  updateFilters: () => false,
  setFilters: () => false,
  setPage: () => false,
  onSort: () => false,

  allDucksReady: false,
  duckMeta: {
    reviewContacts: { ready: false },
    reviewDecisionField: { ready: false },
    userFilter: { ready: false }
  }
};

const mapDucksToProps = ({ reviewYear, page, sort }) => {
  const userFilter = singleDuck(reviewContactsFilters, reviewYear.id);

  /*
   * Fetching reviewContacts directly will return contacts with whatever filters are currently "saved"; this is the
   * default behavior of SchoolAdmin::Review::ReviewContactList. However, doing this will set the "_all" state key to
   * a filtered list and then we'll have no way to fetch "unfiltered" reviewContacts. This fetches the current saved
   * filters first, and then fetches reviewContacts via filterDuck so the stateKey is set correctly.
   */
  const reviewContacts = dependentDuck(userFilter, ({ value }) => {
    const clear = Object.keys(value.filters).length === 0;
    const query = {
      school_year_id: reviewYear.id,
      filters: value.filters,
      view: 'admin',
      meta: true,
      sort,
      page,
      clear
    };

    return filterDuck(reviewContactsDuck, query);
  });

  return {
    userFilter,
    reviewContacts,
    reviewDecisionField: singleDuck(fieldsDuck, 'review_decision')
  };
};

const mapDispatchToProps = dispatch => ({
  updateContact: contact => dispatch(contactsDuck.actions.update(contact, { id: contact.id })),
  /*
   * We don't really need to "update" the filters, that happens automatically when reviewContacts are fetched with new
   * filters. However, we want to update the local state for userFilter to trigger our dependentDuck to re-fetch.
   */
  updateFilters: (userFilter, newFilters) => dispatch(reviewContactsFilters.actions.cacheSingle({
    ...userFilter, filters: newFilters
  }))
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({ column: 'display_name', order: 'asc' }),
  duckify(mapDucksToProps, () => {}, mapDispatchToProps)
)(CandidatesInReviewContactList);
